import { z } from 'zod';
const RSVP_STATUSES = ['invited', 'confirmed', 'declined', 'maybe'];
const SIDES = ['bride', 'groom', 'mutual', 'other'];
const COUNT = z.number().int().min(0).max(50);
/**
 * Guest fields a client may set from the portal. The event comes from the
 * route, and seating/table assignment stays with the producer — a client adds
 * who is coming, not where they sit.
 */
export const portalCreateGuestSchema = z.object({
    firstName: z.string().min(1).max(255),
    lastName: z.string().max(255).optional(),
    familyGroup: z.string().max(255).optional(),
    side: z.enum(SIDES).optional(),
    phone: z.string().max(50).optional(),
    email: z.string().email().max(255).optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    notes: z.string().max(2000).optional(),
});
export const portalUpdateGuestSchema = portalCreateGuestSchema.partial();
/**
 * Extra emails/phones supplied with a guest. Sent as a replace-all list, so
 * omitting the field leaves existing contacts untouched while `[]` clears them.
 */
export const guestContactSchema = z.object({
    id: z.string().uuid().optional(),
    contactType: z.enum(['EMAIL', 'PHONE']),
    value: z.string().trim().min(1).max(255),
    label: z.string().max(100).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const guestContactsSchema = z.array(guestContactSchema).max(20);
export const createGuestSchema = z
    .object({
    contacts: guestContactsSchema.optional(),
    eventId: z.string().uuid(),
    firstName: z.string().min(1).max(255),
    lastName: z.string().max(255).optional(),
    familyGroup: z.string().max(255).optional(),
    side: z.enum(SIDES).optional(),
    phone: z.string().max(50).optional(),
    email: z.string().email().max(255).optional(),
    rsvpStatus: z.enum(RSVP_STATUSES).optional(),
    mealPreference: z.string().max(255).optional(),
    dietaryNotes: z.string().max(2000).optional(),
    tableNumber: z.number().int().min(1).optional(),
    seatNumber: z.number().int().min(1).optional(),
    plusOne: z.boolean().optional(),
    plusOneName: z.string().max(255).optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    keepTogether: z.boolean().nullable().optional(),
    notes: z.string().max(2000).optional(),
    sortOrder: z.number().int().optional(),
})
    .refine((g) => (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0) >= 1, {
    message: 'Guest must include at least one person across male/female/kid counts',
    path: ['maleCount'],
});
export const updateGuestSchema = z
    .object({
    contacts: guestContactsSchema.optional(),
    firstName: z.string().min(1).max(255).optional(),
    lastName: z.string().max(255).nullable().optional(),
    familyGroup: z.string().max(255).nullable().optional(),
    side: z.enum(SIDES).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    email: z.string().email().max(255).nullable().optional(),
    rsvpStatus: z.enum(RSVP_STATUSES).optional(),
    mealPreference: z.string().max(255).nullable().optional(),
    dietaryNotes: z.string().max(2000).nullable().optional(),
    tableNumber: z.number().int().min(1).nullable().optional(),
    seatNumber: z.number().int().min(1).nullable().optional(),
    plusOne: z.boolean().optional(),
    plusOneName: z.string().max(255).nullable().optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    keepTogether: z.boolean().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().optional(),
})
    .refine((g) => {
    // If any of the counts are present, the sum must be >= 1.
    const someCountProvided = g.maleCount !== undefined || g.femaleCount !== undefined || g.kidCount !== undefined;
    if (!someCountProvided)
        return true;
    return (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0) >= 1;
}, {
    message: 'Guest must include at least one person across male/female/kid counts',
    path: ['maleCount'],
});
export const updateRsvpSchema = z.object({
    rsvpStatus: z.enum(RSVP_STATUSES),
});
export const bulkCreateGuestsSchema = z.object({
    guests: z.array(createGuestSchema).min(1).max(500),
});
/**
 * Bulk-update a set of guests with a partial set of field overrides. Used by
 * the guest list's multi-select action bar (Set RSVP, Set Side, etc.). Only
 * fields that make sense to apply uniformly across many rows are exposed.
 */
export const bulkUpdateGuestsSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
    updates: z
        .object({
        rsvpStatus: z.enum(RSVP_STATUSES).optional(),
        side: z.enum(SIDES).nullable().optional(),
        familyGroup: z.string().max(255).nullable().optional(),
        tableNumber: z.number().int().min(1).nullable().optional(),
    })
        .refine((u) => u.rsvpStatus !== undefined ||
        u.side !== undefined ||
        u.familyGroup !== undefined ||
        u.tableNumber !== undefined, { message: 'updates must contain at least one field to apply' }),
});
export const bulkDeleteGuestsSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
});
//# sourceMappingURL=guest.js.map